import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search, Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const pin = L.divIcon({ className: "", iconSize: [30, 30], iconAnchor: [15, 30],
  html: '<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;background:#0052FF;transform:rotate(-45deg);border:3px solid white;box-shadow:0 4px 10px rgba(0,0,0,.35)"></div>' });
const GHANA = [7.95, -1.03];

function Clicks({ onPick }) {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
}

function Recenter({ pos }) {
  const map = useMap();
  useEffect(() => { const id = setTimeout(() => map.invalidateSize(), 350); return () => clearTimeout(id); }, [map]);
  useEffect(() => { if (pos) map.setView(pos, Math.max(map.getZoom(), 14)); }, [pos?.[0], pos?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function LocationPicker({ form, onChange }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const pos = form.lat != null && form.lat !== "" && form.lng != null && form.lng !== "" ? [Number(form.lat), Number(form.lng)] : null;
  const set = async (lat, lng, label) => {
    const next = { lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) };
    if (label) next.address = form.address || label;
    onChange(next);
    if (!label && !form.address) {
      try { const { data } = await api.get("/admin/geo/reverse", { params: { lat, lng } }); if (data.label) onChange({ ...next, address: data.label }); } catch { /* address optional */ }
    }
  };
  const search = async () => {
    if (q.trim().length < 3) return;
    setBusy(true);
    try { const { data } = await api.get("/admin/geo/search", { params: { q } }); setHits(data); if (!data.length) toast.message("No place found - try another name or drop the pin on the map"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="location-picker">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} placeholder="Search address or place, e.g. Kejetia Kumasi" data-testid="location-search-input" />
        <Button type="button" onClick={search} className="bg-[#0052FF] hover:bg-[#0041CC]" data-testid="location-search-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
      </div>
      {hits.length > 0 && (
        <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200" data-testid="location-results">
          {hits.map((h, i) => (
            <button key={i} type="button" onClick={() => { set(h.lat, h.lng, h.label); setHits([]); }} className="flex w-full items-start gap-2 border-b border-slate-100 px-3 py-2 text-left text-xs hover:bg-slate-50" data-testid={`location-result-${i}`}>
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0052FF]" />{h.label}
            </button>
          ))}
        </div>
      )}
      <div className="h-64 overflow-hidden rounded-xl border border-slate-200" data-testid="location-map">
        <MapContainer center={pos || GHANA} zoom={pos ? 14 : 6} className="h-full w-full">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
          <Clicks onPick={(lat, lng) => set(lat, lng)} />
          <Recenter pos={pos} />
          {pos && <Marker position={pos} icon={pin} draggable eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); set(p.lat, p.lng); } }} />}
        </MapContainer>
      </div>
      <p className="text-xs text-slate-500" data-testid="location-coords">{pos ? `Pinned at ${pos[0]}, ${pos[1]} - drag the pin or click the map to adjust.` : "Search above or click on the map to drop the pin."}</p>
    </div>
  );
}
